import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/api';
import { Quote, QuotePatch, QuoteTransit, WarehouseTransitLeg } from '../types/admin';

/**
 * Loads and manages a single quote.
 * Used on detail and edit pages.
 */
export function useQuote(orgId: string | null, quoteId: string | null) {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initialLoadDone = useRef(false);

  const fetchQuote = useCallback(async () => {
    if (!orgId || !quoteId) {
      setLoading(false);
      return;
    }
    // Only show loading spinner on initial load, not background refetches.
    // Setting loading=true on refetch unmounts consuming components, which
    // resets their refs and can cause infinite effect loops.
    if (!initialLoadDone.current) {
      setLoading(true);
    }
    setError(null);
    try {
      const res = await api.get<{ success: boolean; data: Quote }>(`/quotes/${orgId}/${quoteId}`);
      setQuote(res.data);
      initialLoadDone.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load quote');
    } finally {
      setLoading(false);
    }
  }, [orgId, quoteId]);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  const updateQuote = useCallback(
    async (patch: QuotePatch): Promise<Quote> => {
      if (!orgId || !quoteId) throw new Error('No quote loaded');
      const res = await api.patch<{ success: boolean; data: Quote }>(
        `/quotes/${orgId}/${quoteId}`,
        patch,
      );
      setQuote(res.data);
      return res.data;
    },
    [orgId, quoteId],
  );

  const calculateTransit = useCallback(
    async (originPlaceId: string, destinationPlaceId: string): Promise<QuoteTransit> => {
      if (!orgId || !quoteId) throw new Error('No quote loaded');
      const res = await api.post<{ success: boolean; data: QuoteTransit }>(
        `/quotes/${orgId}/${quoteId}/calculate-transit`,
        { originPlaceId, destinationPlaceId },
      );
      // Update local quote state with the new transit data
      setQuote(prev =>
        prev
          ? {
              ...prev,
              addresses: {
                ...(prev.addresses ?? {}),
                transit: res.data,
              },
            }
          : prev,
      );
      return res.data;
    },
    [orgId, quoteId],
  );

  const calculateWarehouseTransits = useCallback(
    async (
      warehouses: Array<{ id: string; placeId: string }>,
      originPlaceId: string,
      destinationPlaceId: string,
    ): Promise<Record<string, { toOrigin?: WarehouseTransitLeg; toDestination?: WarehouseTransitLeg }>> => {
      if (!orgId || !quoteId) throw new Error('No quote loaded');
      const res = await api.post<{
        success: boolean;
        data: Record<string, { toOrigin?: WarehouseTransitLeg; toDestination?: WarehouseTransitLeg }>;
      }>(`/quotes/${orgId}/${quoteId}/calculate-warehouse-transits`, {
        warehouses,
        originPlaceId,
        destinationPlaceId,
      });
      setQuote(prev =>
        prev ? { ...prev, warehouseTransit: res.data } : prev,
      );
      return res.data;
    },
    [orgId, quoteId],
  );

  return { quote, loading, error, updateQuote, calculateTransit, calculateWarehouseTransits, refetch: fetchQuote };
}
